import React, { useState, useEffect, useRef } from "react";
import { Dropdown } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import {
  getWithdrawalRequestsApi,
  processWithdrawalApi,
} from "../../../services/EarningsService";

const STATUS_COLORS = {
  pending: "warning",
  approved: "success",
  rejected: "danger",
  paid: "info",
};

const WithdrawalRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [message, setMessage] = useState({ type: "", text: "" });
  const [processing, setProcessing] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [adminNote, setAdminNote] = useState("");
  const modalRef = useRef(null);

  useEffect(() => {
    fetchRequests();
  }, [statusFilter]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const params = statusFilter !== "all" ? { status: statusFilter } : {};
      const res = await getWithdrawalRequestsApi(params);
      setRequests(res?.data?.data || []);
    } catch (error) {
      setMessage({ type: "danger", text: "Failed to load withdrawal requests." });
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (id, status) => {
    try {
      setProcessing(id);
      await processWithdrawalApi(id, { status, adminNote });
      setMessage({
        type: "success",
        text: `Withdrawal request ${status} successfully.`,
      });
      setSelectedRequest(null);
      setAdminNote("");
      fetchRequests();
    } catch (error) {
      const msg = error?.response?.data?.message || `Failed to ${status} request.`;
      setMessage({ type: "danger", text: msg });
    } finally {
      setProcessing(null);
    }
  };

  const openModal = (req) => {
    setSelectedRequest(req);
    setAdminNote("");
    setMessage({ type: "", text: "" });
  };

  return (
    <>
      <PageTitle activeMenu="Withdrawal Requests" motherMenu="Finance" />

      {message.text && (
        <div className={`alert alert-${message.type} alert-dismissible fade show`}>
          {message.text}
          <button
            type="button"
            className="btn-close"
            onClick={() => setMessage({ type: "", text: "" })}
          />
        </div>
      )}

      <div className="row">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h4 className="card-title">Consultant Withdrawal Requests</h4>
              <div className="d-flex gap-2 align-items-center">
                <label className="mb-0 me-2">Filter:</label>
                <Dropdown>
                  <Dropdown.Toggle variant="outline-primary" size="sm">
                    {statusFilter === "all" ? "All Statuses" : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {["all", "pending", "approved", "rejected", "paid"].map((s) => (
                      <Dropdown.Item key={s} onClick={() => setStatusFilter(s)}>
                        {s === "all" ? "All Statuses" : s.charAt(0).toUpperCase() + s.slice(1)}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
                <button className="btn btn-sm btn-outline-secondary" onClick={fetchRequests}>
                  <i className="fa fa-refresh" />
                </button>
              </div>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" />
                </div>
              ) : requests.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="fa fa-inbox fa-3x mb-3 d-block" />
                  No withdrawal requests found.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Consultant</th>
                        <th>Amount</th>
                        <th>Bank Details</th>
                        <th>Status</th>
                        <th>Date</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requests.map((req) => (
                        <tr key={req.id}>
                          <td>
                            <strong>{req.consultantName || "—"}</strong>
                            <br />
                            <small className="text-muted">{req.consultantId}</small>
                          </td>
                          <td>
                            <strong>₹{req.amount?.toFixed(2)}</strong>
                          </td>
                          <td>
                            <small>
                              {req.bankDetails?.bankName}
                              <br />
                              {req.bankDetails?.accountNumber}
                              <br />
                              IFSC: {req.bankDetails?.ifscCode}
                            </small>
                          </td>
                          <td>
                            <span className={`badge badge-${STATUS_COLORS[req.status] || "secondary"}`}>
                              {req.status}
                            </span>
                          </td>
                          <td>
                            <small>
                              {new Date(req.createdAt).toLocaleDateString("en-IN")}
                            </small>
                          </td>
                          <td>
                            {req.status === "pending" ? (
                              <button
                                className="btn btn-xs btn-primary"
                                onClick={() => openModal(req)}
                              >
                                Review
                              </button>
                            ) : (
                              <span className="text-muted small">
                                {req.adminNote || req.processedAt
                                  ? new Date(req.processedAt).toLocaleDateString("en-IN")
                                  : "—"}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Review Modal */}
      {selectedRequest && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Review Withdrawal Request</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setSelectedRequest(null)}
                />
              </div>
              <div className="modal-body">
                <table className="table table-sm">
                  <tbody>
                    <tr>
                      <td className="fw-bold">Consultant</td>
                      <td>{selectedRequest.consultantName}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Amount</td>
                      <td>₹{selectedRequest.amount?.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Account Holder</td>
                      <td>{selectedRequest.bankDetails?.accountHolderName}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Bank</td>
                      <td>{selectedRequest.bankDetails?.bankName}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Account No.</td>
                      <td>{selectedRequest.bankDetails?.accountNumber}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">IFSC Code</td>
                      <td>{selectedRequest.bankDetails?.ifscCode}</td>
                    </tr>
                  </tbody>
                </table>
                <div className="mb-3">
                  <label className="form-label">Admin Note (optional)</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value)}
                    placeholder="Reason for approval/rejection..."
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setSelectedRequest(null)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  disabled={processing === selectedRequest.id}
                  onClick={() => handleAction(selectedRequest.id, "rejected")}
                >
                  {processing === selectedRequest.id ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    "Reject"
                  )}
                </button>
                <button
                  className="btn btn-success"
                  disabled={processing === selectedRequest.id}
                  onClick={() => handleAction(selectedRequest.id, "approved")}
                >
                  {processing === selectedRequest.id ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    "Approve & Mark Paid"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WithdrawalRequests;
